/**
 * Hook to communicate with the ADP main processs via ADP Electron or Chrome extension bridge
 *
 */
import { Dispatch, useEffect } from "react";
import { IADPState, NetworkConnectionState, UpdateStates, actions } from "../state";
import { IADPActions } from "../state/Actions";
import { ExtensionMessage } from "../../Types";
import { v4 as uuidv4 } from "uuid";
import { isPageReloaded, randomstring } from "../util/util";
import { IpcMessageType, SessionDetails } from "../types/BridgeTypes";
type client = {
  clientCode:string,
  clientVersion:string,
  clientBuildDate:string,
  clientURL:string
}
declare global {
  interface Window {
    adpElectronBridge?: {
      greet: (arg0: client) => Promise<SessionDetails>;
      isUpdateAvailable: () => Promise<IpcMessageType>;
      postChromeAppState:(state:IADPState)=>void;
      lockdownApp:()=>void,
      listenOnNetworkStatusChanges:()=>void
    };
  }
}
export const isElectronBridgeAvailable = () => {
  return window["adpElectronBridge"];
};
export const postMessageToAdpBridge = (message: ExtensionMessage) => {
  //if(window.adp)
  window.postMessage(message);
};
export function useIPCBridge(
  dispatch: Dispatch<IADPActions>,
  state?: IADPState
) {
  const win: Window = window;
  /**
   * send greeting to ADP electron main process or ADP chrome extension.
   * If none is available, application is runnng in the browser without any backend support;
   * >>> Communication with Electron is established via preload.js which is part of the adp electron app and is injected
   * on the app startup. All methods are available at Window scope
   * >>> Communcatioon with Chrome Extension is established via Events that are emitted to the Window's context
   */
  function sendGreeting() {
    //send greeting to Electron and obtain session and version details from the main process, if electron is not available read local manifest file
    if (isElectronBridgeAvailable()) {
      console.log("[ADPClient][IPCBridge] App is running in Electron, send greeting to main process");
      win["adpElectronBridge"]?.greet({
        clientCode:state?.appState.appCode as string,
        clientVersion:state?.appState.appVersion as string,
        clientBuildDate: state?.appState.appBuildDate as string,
        clientURL:window.location.href
      }).then((details: any) => {
        const sessionDetails: SessionDetails = {
          ...details,
          networkConnectionState:details.networkConnectionState && details.networkConnectionState.length > 0 ? details.networkConnectionState[0]?.connectionType : window.navigator.onLine ? "online" : "offline" as NetworkConnectionState,
          isPageReloaded: isPageReloaded(),
          runtimePlatform: {
            platform: "Electron",
            version: details.version,
            userAgent: navigator.userAgent,
          },
        };
        console.log("[ADPClient][IPCBridge] Greeting from Electron, session data ");
        console.table(sessionDetails);
        dispatch(actions.createSetSessionDetailsAction(sessionDetails));
         //subscribe to network events
        win["adpElectronBridge"]?.listenOnNetworkStatusChanges();
      });
    } else {
      console.log("[ADPClient][IPCBridge] App is running in Browser, send greeting to chrome extension"
      );
      //send greeting to chrome extension to check if Chrome extension is available
      const appLaunchId = uuidv4();
      const sessionDetails: SessionDetails = {
        appLaunchId: appLaunchId,
        deviceId: `CB${randomstring(4).toUpperCase()}`, //TODO
        deviceModel: navigator.userAgent, //TODO
        deviceType: "NPE", //TODO
        networkConnectionState:window.navigator.onLine ? "online" : "offline" as NetworkConnectionState,
        isPageReloaded: isPageReloaded(),
        runtimePlatform: {
          platform: "Browser",
          version: "",
          userAgent: navigator.userAgent,
        },
      };
      //TODO - find a better way to check if extension is installed, e.g inject extension version
      const timeoutId = setTimeout(() => {
        console.log("[ADPClient][IPCBridge] extension is not available");
        console.warn('[ADPClient][IPCBridge] app is running with limited functionalites, consider adding ADP Chrome extension for full experience')
        dispatch(actions.createSetSessionDetailsAction(sessionDetails));
      }, 1000);
      console.log("[ADPClient][IPCBridge] send greeting to ADP extension...");
      postMessageToAdpBridge({
        type: "Greeting",
        data: { client: "ADP", appLaunchId: appLaunchId },
      });
      //listen for event from ChrEx
      const handler = (event: any) => {
        console.log(
          "[ADPClient][IPCBridge] Chrome Extension Version:",
          event?.detail?.data?.chromeExtensionVersion
        );
        clearTimeout(timeoutId);
        //get device details 
        postMessageToAdpBridge({
          type: "DeviceDetails"
        });
        const deviceDetailshandler =(event: any)=>{
          let deviceId = `CB${randomstring(4).toUpperCase()}`
          if(event.detail?.data.includes("is not available")){
            console.warn("[ADPClient][IPCBridge]",event.detail?.data)
          }
          else {
            deviceId = event.detail?.data
          }
          const sessionDetails: SessionDetails = {
            deviceId: deviceId, //TODO
            deviceModel: navigator.userAgent, //TODO
            deviceType: "NPE", //TODO
            appLaunchId: appLaunchId,
            isPageReloaded: isPageReloaded(),
            networkConnectionState:window.navigator.onLine ? "online" : "offline" as NetworkConnectionState,
            runtimePlatform: {
              platform: "ChromeWithExtension",
              version: event?.detail?.data?.chromeExtensionVersion,
              userAgent: navigator.userAgent,
            },
          };
          dispatch(actions.createSetSessionDetailsAction(sessionDetails));
          window.removeEventListener("GreetingEvent", handler);
          window.removeEventListener('DeviceDetailsEvent',deviceDetailshandler);
        }
          window.addEventListener('DeviceDetailsEvent', deviceDetailshandler);

      };
      window.addEventListener("GreetingEvent", handler);
    }
  }
  function checkForUpdates() {
    const onUpdateEvents = (e:any) => {
      console.log("[ADPClient][IPCBridge][onUpdateEvents]");
      console.table(e.detail);
      dispatch(actions.createUpdateProgressAction(e.detail));
    };
    window.addEventListener("update-progress",onUpdateEvents);
    win["adpElectronBridge"]
      ?.isUpdateAvailable()
      .then((data: IpcMessageType) => {
        console.log("[ADPClient][IPCBridge]");
        console.table(data);
        const updateType:UpdateStates = data.type;
        window.removeEventListener("update-progress",onUpdateEvents);
        if (updateType === "update-not-available") {
          dispatch(actions.createUpdateProgressAction(data));
          setTimeout(()=>{
            dispatch(actions.createSetViewAction("ModeSelectionsView"));
         },5000);
        } else if (updateType === "update-error") {
          dispatch(actions.createErrorAction("UpdateError"));
        }
        else if (updateType === "update-complete") {
          setTimeout(()=>{
            dispatch(actions.createUpdateProgressAction(data));
         },1000);
        }
      })
      .catch((e) => {
        console.error("[ADPClient][IPCBridge]", e);
        dispatch(actions.createErrorAction("UpdateError"));
        window.removeEventListener("update-progress",onUpdateEvents);
      });
  }
  function saveADPChromeAppState(state:IADPState){
    if (isElectronBridgeAvailable()) {
      win["adpElectronBridge"]?.postChromeAppState(state)
    }
  }
  function startAssessment(){
    win["adpElectronBridge"]?.lockdownApp();
  }
  return { sendGreeting, checkForUpdates,saveADPChromeAppState,startAssessment};
}
